<template>
  <aside id="dashboard" ref="root">
    <div class="container-fixed" :data-test="isCollapsed ? 'dashboardCollapsed' : 'dashboardExpanded'">
      <div class="row offset-bottom-1 aligncontent">
        <div
          v-if="showInformationCustomerData"
          :class="[showInformationContractData ? 'col-l-5 col-m-5' : 'col-l-11 col-m-10']"
          class="col-s-12 col-xs-12 delimiter"
          data-test="customer"
        >
          <transition name="slide-fade" mode="out-in">
            <div v-if="isCollapsed" key="collapsed" class="collapsed limited-flex">
              <slot name="customer-title" />
              <slot name="customer-collapsed" />
            </div>
            <div v-else key="expanded" class="expanded">
              <slot name="customer-title" />
              <slot name="customer-expanded" />
            </div>
          </transition>
        </div>
        <div
          v-if="showInformationContractData"
          :class="[showInformationCustomerData ? 'col-l-6 col-m-5' : 'col-l-11 col-m-10']"
          class="col-l-6 col-s-12 col-xs-12 delimiter"
          data-test="contract"
        >
          <div id="dashboard-contract" :class="{ highlight: contractChanged }">
            <div v-if="contractChanged" role="status" aria-live="polite" class="sr-only">
              {{ $t('common.contractChanged') }}
              <slot name="contract-collapsed" />
            </div>
            <transition name="slide-fade" mode="out-in">
              <div v-if="isCollapsed" key="collapsed" class="collapsed limited-flex">
                <slot name="contract-title" />
                <slot name="contract-collapsed" />
              </div>
              <div v-else key="expanded" class="expanded">
                <slot name="contract-title" />
                <slot name="contract-expanded" />
              </div>
            </transition>
          </div>
        </div>
        <div class="col-l-1 col-m-2 col-s-12 col-xs-12">
          <VCollapsable id="expandDashboard" :collapsed="isCollapsed" @update:collapsed="onToggle" />
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import emitter from '@/plugins/emitter';
import VCollapsable from '@/components/Collapsable';
import { mapGetters } from 'vuex';

export default {
  name: 'DashboardLayout',

  components: {
    VCollapsable,
  },

  data() {
    return {
      contractChanged: false,
    };
  },

  computed: {
    isCollapsed() {
      return this.$store.state.shared.toggle.dashboardCollapsed;
    },

    ...mapGetters('system', ['showInformationCustomerData', 'showInformationContractData']),
  },

  created() {
    emitter.on('contractChanged', this.onContractChange);
  },

  mounted() {
    if (!window.MSInputMethodContext || !document.documentMode) return;
    /* HACK for IE11 */
    const stickyLine = this.$refs.root.parentElement;
    const sticky = stickyLine.parentElement;
    const div = this.$refs.root.querySelector('[data-test="customer"]');

    function setHeight(start) {
      sticky.style.height = start ? '' : `${stickyLine.offsetHeight}px`;
    }

    div.addEventListener(
      'transitionstart',
      function() {
        setHeight(true);
      },
      true
    );
    div.addEventListener(
      'transitionend',
      function() {
        setHeight(false);
      },
      true
    );
  },

  methods: {
    onToggle() {
      this.$store.dispatch('shared/toggle', 'dashboardCollapsed');
    },

    async onContractChange() {
      this.contractChanged = false;
      // Chrome has trouble noticing removing and re-adding classes of dom elements when using nextTick. sleep for 1ms instead.
      await new Promise(resolve => setTimeout(resolve, 1));
      this.contractChanged = true;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

#dashboard {
  background-color: #f6f6f6;
  border-top: none;
  border-bottom: 1px solid $color-border-bright;

  .collapsed {
    color: $font-color-light;
  }

  .expanded {
    color: $color-gray-darker;
  }

  ::v-deep .collapsable-btn {
    margin-top: 5px;
  }
}

@media screen and (min-width: #{$grid-small-max}px) {
  .delimiter {
    border-right: 1px solid $color-border;
  }
}

.limited-flex {
  display: flex;
}

@media screen and (min-width: #{$grid-small-min}px) and (max-width: #{$grid-small-max}px) {
  .limited-flex {
    padding-bottom: $building-unit;
  }
}

@media screen and (max-width: 460px) {
  .limited-flex {
    display: block;
    padding-bottom: $building-unit;
  }
}

#dashboard-contract {
  height: 100%;
  padding: 0 6px 6px;
  margin: 0 -6px -3px;

  &.highlight {
    animation: dashboard-contract-animation 8s 0.5s 1;
  }
}
@keyframes dashboard-contract-animation {
  from {
    background-color: transparent;
  }
  5% {
    background-color: #ff0;
  }
  65% {
    background-color: #ff0;
  }
  to {
    background-color: transparent;
  }
}
</style>
